import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  VerticalAlign,
  WidthType,
} from "docx";
import { saveAs } from "file-saver";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import { unified } from "unified";
import * as XLSX from "xlsx";
import { TOAST_MESSAGES } from "~/constants/messages";
import type { PluginProposalHeaders } from "~/pages/plugin";
import type { RootState } from "~/store";
import { exportElementToPdf } from "~/utils/pdfExport";
import styles from "./index.module.scss";

export interface TableExportData {
  headers: string[] | typeof PluginProposalHeaders;
  rows: string[][];
  name: string;
  markdownContent?: string;
  additionalSheets?: {
    name: string;
    headers: string[];
    rows: string[][];
  }[];
}

export interface DocumentExportData {
  content: string;
}

export interface DrawioExportData {
  xml: string;
  documentType: string;
  drawioRef?: React.RefObject<any>;
}

export interface ReverseEngineeringExportData {
  type: "dependency-analysis" | "requirement-analysis";
  data: any;
}

const getDocumentTypeJapaneseName = (docType: string): string => {
  const typeMapping: Record<string, string> = {
    summary: "議事録",
    "system-proposal": "システム概要書",
    "functional-requirement": "機能要件書",
    "non-functional-requirement": "非機能要件書",
    "plugin-proposal": "プラグイン提案書",
    "field-design": "フィールド設計書",
    "app-design": "アプリ設計書",
    estimation: "概算工数見積もり書",
    "customize-specification": "カスタマイズ仕様書",
    workflow: "業務フロー図",
    "system-architecture": "システム構成図",
    "entity-relationship": "ER図",
    "dependency-analysis": "依存関係分析",
    "requirement-analysis": "要件分析",
  };

  return typeMapping[docType] || docType; // Return original value if no mapping exists
};

const sanitizeFilename = (name: string): string => {
  if (!name) return "エクスポート";
  const sanitized = name
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .trim();
  return sanitized || "エクスポート";
};

export const exportToExcel = (data: TableExportData, filename: string, name: string) => {
  if (filename === "") {
    filename = "untitled";
  }
  const workbook = XLSX.utils.book_new();
  const markdownTables = data.markdownContent
    ? parseMarkdownTablesFromContent(data.markdownContent)
    : [];

  const appendAdditionalSheets = (
    startIndex: number,
    usedSheetNames: Set<string>,
    defaultSheetTitle: string,
  ) => {
    if (!data.additionalSheets || data.additionalSheets.length === 0) return;

    data.additionalSheets.forEach((sheet, sheetIndex) => {
      const headers = ensureHeaders(sheet.headers, sheet.rows);
      const normalizedRows = sheet.rows.map((row) => normalizeRowLength(row, headers.length));
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...normalizedRows]);
      const columnWidths = headers.map((header, columnIndex) => {
        const maxLength = Math.max(
          header.length,
          ...normalizedRows.map((row) => (row[columnIndex] || "").length),
        );
        return { wch: Math.min(maxLength + 2, 60) };
      });
      worksheet["!cols"] = columnWidths;
      const sheetName = sanitizeSheetName(
        sheet.name,
        defaultSheetTitle,
        usedSheetNames,
        startIndex + sheetIndex,
      );
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });
  };

  if (markdownTables.length > 0) {
    const usedSheetNames = new Set<string>();
    const defaultSheetTitle = getDocumentTypeJapaneseName(name);

    markdownTables.forEach((table, index) => {
      const headers = ensureHeaders(table.headers, table.rows);
      const normalizedRows = table.rows.map((row) => normalizeRowLength(row, headers.length));

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...normalizedRows]);

      const columnWidths = headers.map((header, columnIndex) => {
        const maxLength = Math.max(
          header.length,
          ...normalizedRows.map((row) => (row[columnIndex] || "").length),
        );
        return { wch: Math.min(maxLength + 2, 60) };
      });
      worksheet["!cols"] = columnWidths;

      const sheetName = sanitizeSheetName(table.title, defaultSheetTitle, usedSheetNames, index);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    appendAdditionalSheets(markdownTables.length, usedSheetNames, defaultSheetTitle);

    XLSX.writeFile(workbook, `${filename}.xlsx`);
    return;
  }

  if (data.markdownContent && markdownTables.length === 0) {
    const usedSheetNames = new Set<string>();
    const defaultSheetTitle = getDocumentTypeJapaneseName(name);
    const sheetName = sanitizeSheetName(defaultSheetTitle, defaultSheetTitle, usedSheetNames, 0);
    const markdownLines = data.markdownContent.split(/\r?\n/);
    const worksheet = XLSX.utils.aoa_to_sheet([
      ["Markdown"],
      ...markdownLines.map((line) => [line]),
    ]);
    const maxLength = markdownLines.reduce(
      (max, line) => Math.max(max, line.length),
      "Markdown".length,
    );
    worksheet["!cols"] = [{ wch: Math.min(maxLength + 2, 80) }];
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    appendAdditionalSheets(1, usedSheetNames, defaultSheetTitle);
    XLSX.writeFile(workbook, `${filename}.xlsx`);
    return;
  }

  if (data.additionalSheets && data.additionalSheets.length > 0) {
    const mainSheet = XLSX.utils.json_to_sheet(
      data.rows.map((row) => {
        const obj: { [key: string]: string } = {};
        data.headers.forEach((header, index) => {
          obj[header] = row[index] || "";
        });
        return obj;
      }),
    );

    // メインシート名の決定（アクセス権限設計書の場合は特別処理）
    let mainSheetName = data.name;
    if (name === "アクセス権限設計書") {
      mainSheetName = "アプリ権限";
    } else if (mainSheetName.length > 30) {
      mainSheetName = `${mainSheetName.substring(0, 27)}...`;
    }

    XLSX.utils.book_append_sheet(workbook, mainSheet, mainSheetName);

    // 追加シートの処理
    data.additionalSheets.forEach((sheet) => {
      const additionalSheet = XLSX.utils.json_to_sheet(
        sheet.rows.map((row) => {
          const obj: { [key: string]: string } = {};
          sheet.headers.forEach((header, index) => {
            obj[header] = row[index] || "";
          });
          return obj;
        }),
      );

      // シート名の長さを確認して必要に応じて切り詰める
      let sheetName = sheet.name;
      if (sheetName.length > 30) {
        sheetName = `${sheetName.substring(0, 27)}...`;
      }

      XLSX.utils.book_append_sheet(workbook, additionalSheet, sheetName);
    });
  } else if (name === "app-design") {
    // シート1: アプリテーブル
    const appTableData = data.rows.filter((row) => row.length === 3); // アプリテーブルデータ
    const appHeaders = ["アプリID", "アプリ名", "説明"];
    const appSheet = XLSX.utils.json_to_sheet(
      appTableData.map((row) => ({
        [appHeaders[0]!]: row[0],
        [appHeaders[1]!]: row[1],
        [appHeaders[2]!]: row[2],
      })),
    );
    XLSX.utils.book_append_sheet(workbook, appSheet, "作成するアプリ");

    // シート2: アプリ間連携テーブル
    const connectorTableData = data.rows.filter((row) => row.length === 6 && row[0] !== ""); // アプリ間連携テーブルデータ
    const connectorHeaders = [
      "連携ID",
      "データ元アプリID",
      "連携先アプリID",
      "連携方法",
      "キーフィールド",
      "参照フィールド",
    ];
    const connectorSheet = XLSX.utils.json_to_sheet(
      connectorTableData.map((row) => ({
        [connectorHeaders[0]!]: row[0],
        [connectorHeaders[1]!]: row[1],
        [connectorHeaders[2]!]: row[2],
        [connectorHeaders[3]!]: row[3],
        [connectorHeaders[4]!]: row[4],
        [connectorHeaders[5]!]: row[5],
      })),
    );
    XLSX.utils.book_append_sheet(workbook, connectorSheet, "アプリ間の連携");

    // シート3: 要求機能テーブル
    const requirementTableData = data.rows.filter((row) => row.length === 5); // 要求機能テーブルデータ
    const requirementHeaders = ["アプリID", "機能ID", "機能", "説明", "実装方法"];
    const requirementSheet = XLSX.utils.json_to_sheet(
      requirementTableData.map((row) => ({
        [requirementHeaders[0]!]: row[0],
        [requirementHeaders[1]!]: row[1],
        [requirementHeaders[2]!]: row[2],
        [requirementHeaders[3]!]: row[3],
        [requirementHeaders[4]!]: row[4],
      })),
    );
    XLSX.utils.book_append_sheet(workbook, requirementSheet, "アプリ毎の機能");
    // シート4: 考慮点
    const considerationPoints = data.rows.filter((row) => row.length === 1); // 考慮点のデータ
    const considerationSheet = XLSX.utils.json_to_sheet(
      considerationPoints.map((point, index) => ({
        考慮点: `${index + 1}`,
        内容: point[0],
      })),
    );
    XLSX.utils.book_append_sheet(workbook, considerationSheet, "考慮点");
  } else if (name === "field-design") {
    // アプリIDごとにデータをグループ化
    const groupedByAppId = data.rows.reduce(
      (acc, row) => {
        if (row.length >= 6) {
          const appId = row[0]; // app_id
          if (appId !== undefined) {
            if (!acc[appId]) acc[appId] = [];
            acc[appId]!.push(row);
          }
        }
        return acc;
      },
      {} as Record<string, string[][]>,
    );

    // アプリ名情報を取得
    // フィールド設計ページから渡されたヘッダー情報を活用
    const appNameIndex = data.headers.findIndex(
      (header) => header === "app_name" || header.includes("アプリ名") || header === "app_name",
    );

    const appInfo: Record<string, string> = {};

    // 最初にグループ化されたデータをループして、各アプリIDのサンプルデータを取得
    Object.entries(groupedByAppId).forEach(([appId, rows]) => {
      if (rows.length > 0) {
        // 各アプリごとに最初の行からアプリ名を抽出
        const firstRow = rows[0];
        if (firstRow && firstRow.length > 0) {
          // まずアプリIDを設定（デフォルト値）
          appInfo[appId] = `アプリ ${appId}`;

          // ヘッダーにapp_nameまたはアプリ名が含まれていればそれを使用
          if (appNameIndex !== -1 && firstRow.length > appNameIndex) {
            if (firstRow[appNameIndex] && typeof firstRow[appNameIndex] === "string") {
              appInfo[appId] = firstRow[appNameIndex]!;
            }
          }
        }
      }
    });

    // グローバルなアプリデータからも情報を補完
    try {
      // @ts-expect-error - アプリデータが利用可能な場合のみアクセス
      if (window && Object.hasOwn(window, "appData") && window.appData) {
        // @ts-expect-error - アプリデータへのアクセス
        window.appData.forEach((app: any) => {
          if (app.app_id && app.app_name) {
            // 既存の情報がない場合のみ上書き
            if (!appInfo[app.app_id] || appInfo[app.app_id] === `アプリ ${app.app_id}`) {
              appInfo[app.app_id] = app.app_name;
            }
          }
        });
      }
    } catch (e) {
      console.error("アプリ名の取得に失敗しました", e);
    }

    // 共通ヘッダーの定義
    const commonHeaders = [
      "フィールド名",
      "フィールドコード",
      "フィールドタイプ",
      "必須",
      "ラベル表示",
      "詳細設定",
    ];

    // 各アプリIDごとにシートを作成
    Object.entries(groupedByAppId).forEach(([appId, rows]) => {
      // シート名の設定（アプリ名を使用）
      const appName = appInfo[appId] || `アプリ ${appId}`;
      const sheetName = appName.length > 30 ? `${appName.substring(0, 27)}...` : appName;

      // 行データの変換
      const sheetData = rows.map((row) => {
        // 基本情報の抽出
        const fieldName = row[2]; // フィールド名
        const fieldCode = row[3]; // フィールドコード
        const fieldType = row[4]; // フィールドタイプ
        const required = row[5] === "true"; // 必須
        const noLabel = row[6] === "true"; // ラベル表示しない

        // サブテーブルの場合は詳細設定セルにsubFields情報をそのまま出力
        let detailSetting = "";
        if (fieldType === "SUBTABLE") {
          // row[7]以降にsubFields情報が入っている想定
          detailSetting = row[7] || "（設定なし）";
        } else {
          // フィールドタイプに応じた特殊設定を抽出
          const typeSpecificSettings: Record<string, any> = {};
          if (
            fieldType === "SINGLE_LINE_TEXT" ||
            fieldType === "MULTI_LINE_TEXT" ||
            fieldType === "RICH_TEXT"
          ) {
            if (row[7]) typeSpecificSettings["minLength"] = row[7];
            if (row[8]) typeSpecificSettings["maxLength"] = row[8];
            if (row[9]) typeSpecificSettings["defaultValue"] = row[9];
            if (row[10]) typeSpecificSettings["unique"] = row[10] === "true";
          } else if (fieldType === "NUMBER") {
            if (row[7]) typeSpecificSettings["minValue"] = row[7];
            if (row[8]) typeSpecificSettings["maxValue"] = row[8];
            if (row[9]) typeSpecificSettings["unit"] = row[9];
            if (row[10]) typeSpecificSettings["unitPosition"] = row[10];
            if (row[11]) typeSpecificSettings["digit"] = row[11] === "true";
            if (row[12]) typeSpecificSettings["displayScale"] = row[12];
          } else if (fieldType === "DATE" || fieldType === "DATETIME") {
            if (row[7]) typeSpecificSettings["defaultNowValue"] = row[7] === "true";
          } else if (
            fieldType &&
            ["DROP_DOWN", "RADIO_BUTTON", "CHECK_BOX", "MULTI_SELECT"].includes(fieldType)
          ) {
            try {
              if (row[7]) {
                const options = JSON.parse(row[7]);
                typeSpecificSettings["options"] = options;
              }
            } catch (_e) {
              typeSpecificSettings["options"] = row[7] || [];
            }
          } else if (fieldType === "LOOKUP") {
            try {
              if (row[7]) typeSpecificSettings["relatedApp"] = JSON.parse(row[7]);
              if (row[8]) typeSpecificSettings["relatedKeyField"] = row[8];
              if (row[9]) typeSpecificSettings["fieldMappings"] = JSON.parse(row[9]);
              if (row[10]) typeSpecificSettings["lookupPickerFields"] = JSON.parse(row[10]);
              if (row[11]) typeSpecificSettings["sort"] = JSON.parse(row[11]);
            } catch (_e) {}
          } else if (fieldType === "REFERENCE_TABLE") {
            try {
              if (row[7]) typeSpecificSettings["relatedApp"] = JSON.parse(row[7]);
              if (row[8]) typeSpecificSettings["condition"] = JSON.parse(row[8]);
              if (row[9]) typeSpecificSettings["displayFields"] = JSON.parse(row[9]);
              if (row[10]) typeSpecificSettings["sort"] = JSON.parse(row[10]);
              if (row[11]) typeSpecificSettings["size"] = JSON.parse(row[11]);
            } catch (_e) {}
          } else if (fieldType === "LINK") {
            if (row[7]) typeSpecificSettings["protocol"] = row[7];
          } else if (fieldType === "USER_SELECT") {
            if (row[5]) typeSpecificSettings["noLabel"] = JSON.parse(row[5]);
            if (row[6]) typeSpecificSettings["openGroup"] = JSON.parse(row[6]);
          } else if (fieldType === "ORGANIZATION_SELECT") {
            if (row[5]) typeSpecificSettings["noLabel"] = JSON.parse(row[5]);
            if (row[6]) typeSpecificSettings["required"] = JSON.parse(row[6]);
          } else if (fieldType === "GROUP_SELECT") {
            if (row[5]) typeSpecificSettings["noLabel"] = JSON.parse(row[5]);
            if (row[6]) typeSpecificSettings["required"] = JSON.parse(row[6]);
          } else if (fieldType === "FILE") {
            if (row[7]) typeSpecificSettings["thumbnailSize"] = row[7];
          } else if (fieldType === "GROUP") {
            if (row[5]) typeSpecificSettings["noLabel"] = JSON.parse(row[5]);
            if (row[6]) typeSpecificSettings["required"] = JSON.parse(row[6]);
          }
          detailSetting = formatSettings(typeSpecificSettings);
        }
        // フィールドタイプを日本語に変換
        const jaFieldType = getFieldTypeJapaneseName(fieldType || "");
        // データ行に情報を挿入（アプリID、アプリ名、フィールドIDを除外）
        return {
          フィールド名: fieldName,
          フィールドコード: fieldCode,
          フィールドタイプ: jaFieldType,
          必須: required ? "○" : "×",
          ラベル表示: noLabel ? "×" : "○",
          詳細設定: detailSetting,
        };
      });

      // シートを作成して追加
      const worksheet = XLSX.utils.json_to_sheet(sheetData, { header: commonHeaders });

      // 列幅の調整（アプリID、アプリ名、フィールドIDを除外）
      const columnWidths = [
        { wch: 20 }, // フィールド名
        { wch: 20 }, // フィールドコード
        { wch: 15 }, // フィールドタイプ
        { wch: 8 }, // 必須
        { wch: 10 }, // ラベル表示
        { wch: 60 }, // 詳細設定
      ];
      worksheet["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });
  } else {
    // デフォルト: 単一シート
    const exportData = data.rows.map((row) =>
      row.reduce(
        (acc, cell, index) => {
          acc[data.headers[index]!] = cell;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const wsColInfo = [];
    for (let i = 0; i < data.headers.length; i++) {
      let maxWidth = data.headers[i]!.length;
      data.rows.forEach((row) => {
        const cellValue = String(row[i] || "");
        if (cellValue.length > maxWidth) maxWidth = cellValue.length;
      });
      wsColInfo.push({ wch: maxWidth + 2 }); // 少し余裕を持たせる
    }
    worksheet["!cols"] = wsColInfo;

    const wsRowInfo = exportData.map(() => ({ hpt: -1 })); // -1は自動調整を意味します
    worksheet["!rows"] = wsRowInfo;

    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  }

  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

// オブジェクトか文字列かを判別してパース
const safeParseContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return content; // パースできない場合はそのまま返す
  }
};

interface ParsedMarkdownTable {
  headers: string[];
  rows: string[][];
  title?: string | undefined;
}

const getNodePlainText = (node: any): string => {
  if (!node) return "";
  if (Array.isArray(node)) {
    return node.map(getNodePlainText).join("");
  }

  if (typeof node.value === "string") {
    return node.value;
  }

  if (node.type === "break" || node.type === "thematicBreak") {
    return "\n";
  }

  if (node.children && Array.isArray(node.children)) {
    return node.children.map(getNodePlainText).join("");
  }

  return "";
};

const sanitizeSheetName = (
  desiredName: string | undefined,
  fallbackName: string,
  usedNames: Set<string>,
  index: number,
): string => {
  const INVALID_CHAR_PATTERN = /[\\/?*[\]:]/g;
  const fallback = fallbackName || `Sheet${index + 1}`;
  let baseName = desiredName?.trim() || fallback;
  baseName = baseName.replace(INVALID_CHAR_PATTERN, " ");
  if (!baseName) {
    baseName = `Sheet${index + 1}`;
  }

  const MAX_LENGTH = 31;
  if (baseName.length > MAX_LENGTH) {
    baseName = baseName.substring(0, MAX_LENGTH);
  }

  let uniqueName = baseName;
  let suffix = 1;
  while (usedNames.has(uniqueName)) {
    const suffixText = `_${suffix}`;
    const allowedLength = MAX_LENGTH - suffixText.length;
    uniqueName = `${baseName.substring(0, Math.max(allowedLength, 0))}${suffixText}`;
    suffix += 1;
  }

  usedNames.add(uniqueName);
  return uniqueName;
};

const ensureHeaders = (headers: string[], rows: string[][]): string[] => {
  const trimmedHeaders = headers.map((header) => header.trim());
  const hasHeaderContent = trimmedHeaders.some((header) => header.length > 0);
  const maxColumns = Math.max(trimmedHeaders.length, ...rows.map((row) => row.length));

  if (maxColumns === 0) {
    return ["Column 1"];
  }

  const normalizedHeaders = hasHeaderContent
    ? [...trimmedHeaders]
    : Array.from({ length: maxColumns }, (_, idx) => `Column ${idx + 1}`);

  if (normalizedHeaders.length < maxColumns) {
    const additional = Array.from(
      { length: maxColumns - normalizedHeaders.length },
      (_, idx) => `Column ${normalizedHeaders.length + idx + 1}`,
    );
    normalizedHeaders.push(...additional);
  }

  return normalizedHeaders.slice(0, maxColumns);
};

const normalizeRowLength = (row: string[], targetLength: number): string[] => {
  if (row.length >= targetLength) {
    return row.slice(0, targetLength);
  }
  return [...row, ...Array.from({ length: targetLength - row.length }, () => "")];
};

const parseMarkdownTablesFromContent = (markdown: string): ParsedMarkdownTable[] => {
  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown);
  const tables: ParsedMarkdownTable[] = [];
  const headingStack: string[] = [];

  const visitNodes = (nodes: any[]) => {
    if (!Array.isArray(nodes)) return;

    nodes.forEach((node) => {
      if (node.type === "heading") {
        const headingText = getNodePlainText(node).trim();
        if (headingText) {
          headingStack[node.depth - 1] = headingText;
          headingStack.length = node.depth;
        }
      } else if (node.type === "table") {
        const headerRow = node.children?.[0];
        const headers = (headerRow?.children || []).map((cell: any) =>
          getNodePlainText(cell).replace(/\s+/g, " ").trim(),
        );

        const dataRows = (node.children || [])
          .slice(1)
          .map((row: any) =>
            (row.children || []).map((cell: any) =>
              getNodePlainText(cell).replace(/\s+/g, " ").trim(),
            ),
          );

        const title = headingStack[headingStack.length - 1];
        tables.push({ headers, rows: dataRows, title });
      }

      if (node.children) {
        visitNodes(node.children);
      }
    });
  };

  visitNodes(tree.children || []);
  return tables;
};

const parseMarkdownToDocx = (markdownAst: any): (Paragraph | Table)[] => {
  const paragraphs: (Paragraph | Table)[] = [];

  const parseNode = (node: any, listLevel: number = 0) => {
    switch (node.type) {
      case "heading":
        paragraphs.push(
          new Paragraph({
            text: node.children.map((child: any) => child.value || "").join(" "),
            heading:
              node.depth === 1
                ? HeadingLevel.HEADING_1
                : node.depth === 2
                  ? HeadingLevel.HEADING_2
                  : HeadingLevel.HEADING_3,
          }),
        );
        break;

      case "paragraph":
        paragraphs.push(
          new Paragraph({
            text: node.children.map((child: any) => child.value || "").join(" "),
          }),
        );
        break;

      case "list":
        node.children.forEach((item: any) => parseNode(item, listLevel + 1));
        break;

      case "listItem":
        paragraphs.push(
          new Paragraph({
            text: `${"  ".repeat(listLevel)}- ${node.children
              .map((child: any) =>
                child.type === "paragraph"
                  ? child.children.map((c: any) => c.value || "").join(" ")
                  : "",
              )
              .join(" ")}`,
          }),
        );
        node.children.forEach((child: any) => {
          if (child.type === "list") parseNode(child, listLevel + 1);
        });
        break;

      case "text":
        paragraphs.push(
          new Paragraph({
            text: node.value || "",
          }),
        );
        break;

      case "table": {
        const headers = node.children[0]?.children.map(
          (headerNode: any) =>
            new TableCell({
              children: [
                new Paragraph(headerNode.children.map((c: any) => c.value || "").join(" ")),
              ],
            }),
        );

        const rows = node.children.slice(1).map(
          (rowNode: any) =>
            new TableRow({
              children: rowNode.children.map(
                (cellNode: any) =>
                  new TableCell({
                    children: [
                      new Paragraph(cellNode.children.map((c: any) => c.value || "").join(" ")),
                    ],
                  }),
              ),
            }),
        );

        if (headers) {
          paragraphs.push(new Table({ rows: [new TableRow({ children: headers }), ...rows] }));
        }
        break;
      }

      default:
        if (node.children) {
          node.children.forEach((child: any) => parseNode(child, listLevel));
        }
        break;
    }
  };

  parseNode(markdownAst);
  return paragraphs;
};

// Docxファイルとしてエクスポート
export const exportToDocx = async (content: string, filename: string) => {
  try {
    const parsedContent = safeParseContent(content);
    const paragraphs =
      typeof parsedContent === "object"
        ? traverseObjectToDocx(parsedContent)
        : parseMarkdownToDocx(unified().use(remarkParse).use(remarkGfm).parse(content));

    const doc = new Document({
      sections: [{ children: paragraphs }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${filename || "untitled"}.docx`);
  } catch (_error) {}
};

const parseMarkdownTableToDocx = (headers: string[], rows: string[][]): Table => {
  const calculateCellWidth = (text: string): number => {
    const charWidth = 300; // 1文字あたりの幅（DXA単位）
    const calculatedWidth = text.length * charWidth;
    const maxWidth = 2000; // 最大幅の設定（DXA単位）
    return Math.min(calculatedWidth, maxWidth);
  };

  const columnWidths = headers.map((header) => calculateCellWidth(header));

  const createWrappedParagraphs = (text: string): Paragraph[] => {
    const maxCharsPerLine = 20; // 1行あたりの最大文字数
    if (text.length <= maxCharsPerLine) {
      return [new Paragraph({ text, wordWrap: true })];
    }

    const paragraphs: Paragraph[] = [];
    for (let i = 0; i < text.length; i += maxCharsPerLine) {
      const chunk = text.substr(i, maxCharsPerLine);
      paragraphs.push(new Paragraph({ text: chunk, wordWrap: true }));
    }
    return paragraphs;
  };

  const tableRows = [
    new TableRow({
      children: headers.map(
        (header, index) =>
          new TableCell({
            children: createWrappedParagraphs(header),
            width: { size: columnWidths[index] || 2000, type: WidthType.DXA },
            verticalAlign: VerticalAlign.CENTER,
            margins: { top: 100, bottom: 100, left: 100, right: 100 },
          }),
      ),
    }),
    ...rows.map(
      (row) =>
        new TableRow({
          children: row.map(
            (cell, index) =>
              new TableCell({
                children: createWrappedParagraphs(cell),
                width: { size: columnWidths[index] || 2000, type: WidthType.DXA },
                verticalAlign: VerticalAlign.CENTER,
                margins: { top: 100, bottom: 100, left: 100, right: 100 },
              }),
          ),
        }),
    ),
  ];

  return new Table({
    rows: tableRows,
    width: { size: 9000, type: WidthType.DXA }, // テーブル全体幅を固定値で指定
    columnWidths: columnWidths, // 各列の幅を指定
  });
};

// Markdownエクスポート
export const exportToMarkdown = (data: DocumentExportData, filename: string) => {
  try {
    const parsedContent = safeParseContent(data.content);
    const markdownContent =
      typeof parsedContent === "object" ? convertObjectToMarkdown(parsedContent) : parsedContent;

    const blob = new Blob([markdownContent], {
      type: "text/markdown;charset=utf-8",
    });
    saveAs(blob, `${filename || "untitled"}.md`);
  } catch (_error) {}
};

/// JSONオブジェクトを再帰的にDocx構造に変換
const traverseObjectToDocx = (
  obj: Record<string, any>,
  level: number = 1,
): (Paragraph | Table)[] => {
  const paragraphs: (Paragraph | Table)[] = [];
  for (const [key, value] of Object.entries(obj)) {
    paragraphs.push(
      new Paragraph({
        text: key,
        heading: level === 1 ? HeadingLevel.HEADING_1 : HeadingLevel.HEADING_2,
      }),
    );

    if (typeof value === "string") {
      paragraphs.push(new Paragraph({ text: value }));
    } else if (Array.isArray(value)) {
      value.forEach((item) => {
        if (typeof item === "string") {
          paragraphs.push(new Paragraph({ text: `- ${item}` }));
        } else if (typeof item === "object") {
          paragraphs.push(...traverseObjectToDocx(item, level + 1));
        }
      });
    } else if (typeof value === "object") {
      if (value.headers && value.rows) {
        paragraphs.push(parseMarkdownTableToDocx(value.headers, value.rows));
      } else {
        paragraphs.push(...traverseObjectToDocx(value, level + 1));
      }
    }
  }
  return paragraphs;
};

// JSON -> Markdown
const convertObjectToMarkdown = (obj: Record<string, any>, level: number = 1): string => {
  let markdownContent = "";
  for (const [key, value] of Object.entries(obj)) {
    markdownContent += `${"#".repeat(level)} ${key}\n\n`;
    if (typeof value === "string") {
      markdownContent += `${value}\n\n`;
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === "string") {
          markdownContent += `- ${item}\n`;
        } else if (typeof item === "object") {
          markdownContent += convertObjectToMarkdown(item, level + 1);
        }
      }
    } else if (typeof value === "object") {
      if (value.headers && value.rows) {
        markdownContent += `${generateMarkdownTable(value.headers, value.rows)}\n\n`;
      } else {
        markdownContent += convertObjectToMarkdown(value, level + 1);
      }
    }
  }
  return markdownContent;
};

const generateMarkdownTable = (headers: string[], rows: string[][]): string => {
  let markdownTable = `| ${headers.join(" | ")} |\n`;
  markdownTable += `| ${headers.map(() => "---").join(" | ")} |\n`;
  rows.forEach((row) => {
    markdownTable += `| ${row.join(" | ")} |\n`;
  });
  return markdownTable;
};

const convertTableDataToMarkdown = (data: TableExportData): string => {
  if (data.markdownContent) {
    return data.markdownContent;
  }
  return generateMarkdownTable(data.headers as string[], data.rows);
};

// フィールドタイプを日本語に変換する関数
const getFieldTypeJapaneseName = (fieldType: string): string => {
  const typeMapping: Record<string, string> = {
    SINGLE_LINE_TEXT: "文字列（1行）",
    MULTI_LINE_TEXT: "文字列（複数行）",
    RICH_TEXT: "リッチエディター",
    NUMBER: "数値",
    CALC: "計算",
    DATE: "日付",
    TIME: "時刻",
    DATETIME: "日時",
    DROP_DOWN: "ドロップダウン",
    RADIO_BUTTON: "ラジオボタン",
    MULTI_SELECT: "複数選択",
    CHECK_BOX: "チェックボックス",
    USER_SELECT: "ユーザー選択",
    ORGANIZATION_SELECT: "組織選択",
    GROUP_SELECT: "グループ選択",
    LINK: "リンク",
    FILE: "添付ファイル",
    IMAGE: "画像",
    LOOKUP: "ルックアップ",
    REFERENCE_TABLE: "関連レコード一覧",
    STATUS: "ステータス",
    ASSIGNEE: "担当者",
    CREATOR: "作成者",
    CREATED_TIME: "作成日時",
    UPDATED_TIME: "更新日時",
    SUBTABLE: "テーブル",
    RECORD_NUMBER: "レコード番号",
    MODIFIER: "更新者",
    GROUP: "グループ",
    STATUS_ASSIGNEE: "作業者",
    CATEGORY: "カテゴリー",
    CHECKBOX: "チェックボックス",
  };

  return typeMapping[fieldType] || fieldType;
};

// 設定情報を読みやすいフォーマットに変換する関数
export const formatSettings = (settings: Record<string, any>): string => {
  if (!settings || Object.keys(settings).length === 0) return "（設定なし）";

  const keyMapping: Record<string, string> = {
    required: "必須",
    unique: "値の重複を禁止する",
    defaultValue: "デフォルト値",
    minValue: "最小値",
    maxValue: "最大値",
    maxLength: "最大文字数",
    minLength: "最小文字数",
    defaultNowValue: "現在の日時をデフォルト値にする",
    unit: "単位",
    unitPosition: "単位の位置",
    digit: "桁区切りを表示する",
    displayScale: "表示スケール",
    selectionItems: "選択肢",
    userSelectionItems: "ユーザー選択肢",
    protocol: "入力値の種類",
    relatedApp: "関連アプリ",
    condition: "表示するレコードの条件",
    fieldMappings: "他のフィールドのコピー",
    lookupPickerFields: "ルックアップ選択時に表示するフィールド",
    noLabel: "フィールド名を表示しない",
    openGroup: "グループを開く",
    thumbnailSize: "サムネイルサイズ",
    options: "選択肢",
    size: "表示件数",
    displayFields: "表示するフィールド",
    sort: "ソート条件",
  };

  return Object.entries(settings)
    .map(([key, value]) => {
      const displayKey = keyMapping[key] || key;
      if (key === "relatedApp" && typeof value === "object" && value !== null) {
        return `${displayKey}: ${value.code}`;
      }
      if (key === "condition" && typeof value === "object" && value !== null) {
        if (Array.isArray(value)) {
          return `${displayKey}: ${value.map((v) => `${v.field} = ${v.relatedField}`).join(", ")}`;
        } else if (value.field && value.relatedField) {
          return `${displayKey}: ${value.field} = ${value.relatedField}`;
        }
      }
      if (key === "fieldMappings" && typeof value === "object" && value !== null) {
        if (Array.isArray(value)) {
          return `${displayKey}: ${value.map((v) => `${v.field} ← ${v.relatedField}`).join(", ")}`;
        } else if (value.field && value.relatedField) {
          return `${displayKey}: ${value.field} ← ${value.relatedField}`;
        }
      }
      if (Array.isArray(value)) {
        if (value.length === 0) return `${displayKey}: なし`;
        return `${displayKey}: ${value
          .map((item: any) => {
            if (typeof item === "object") {
              return item.name || item.label || JSON.stringify(item);
            }
            return item;
          })
          .join(", ")}`;
      } else if (typeof value === "object" && value !== null) {
        if (Object.keys(value).length === 0) return `${displayKey}: なし`;
        return `${displayKey}: ${JSON.stringify(value)}`;
      } else if (typeof value === "boolean") {
        return `${displayKey}: ${value ? "はい" : "いいえ"}`;
      } else if (value === "" || value === null || value === undefined) {
        return null;
      }
      return `${displayKey}: ${value}`;
    })
    .filter((item) => item !== null)
    .join("\n");
};

interface ImportSelectionModalProps {
  documentType: string;
  onClose: () => void;
  exportData:
    | { type: "table"; exportData: TableExportData; name: string }
    | { type: "document"; exportData: DocumentExportData; name: string }
    | { type: "drawio"; exportData: DrawioExportData; name: string }
    | { type: "reverse-engineering"; exportData: ReverseEngineeringExportData; name: string };
}

const ExportModal: React.FC<ImportSelectionModalProps> = ({
  documentType: _documentType,
  onClose,
  exportData,
}) => {
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const defaultBaseName = useMemo(() => {
    const base = getDocumentTypeJapaneseName(exportData.name) || "エクスポート";
    const projectPart = currentProject?.name ? `_${currentProject.name}` : "";
    return sanitizeFilename(`${base}${projectPart}`);
  }, [exportData.name, currentProject?.name]);

  const [filename, setFilename] = useState(defaultBaseName);

  useEffect(() => {
    setFilename(defaultBaseName);
  }, [defaultBaseName]);

  const resolvedFilename = useMemo(() => {
    const inputName = sanitizeFilename(filename);
    return inputName || defaultBaseName || "エクスポート";
  }, [filename, defaultBaseName]);
  const handleExportMarkdown = async () => {
    if (exportData.type === "table" && exportData.exportData && currentProject && currentUser) {
      const markdownContent = convertTableDataToMarkdown(exportData.exportData);
      const blob = new Blob([markdownContent], { type: "text/markdown;charset=utf-8" });
      saveAs(blob, `${resolvedFilename}.md`);
      toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
    } else if (
      exportData.type === "document" &&
      exportData.exportData &&
      currentProject &&
      currentUser
    ) {
      exportToMarkdown(exportData.exportData, resolvedFilename);
      toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.EXPORT.MISSING_INFO);
    }
  };

  const handleExportDocx = async () => {
    if (exportData.type === "document" && exportData.exportData && currentProject && currentUser) {
      exportToDocx(exportData.exportData.content, resolvedFilename);
      toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.EXPORT.MISSING_INFO);
    }
  };

  const handleExportXlsx = async () => {
    if (exportData.type === "table" && exportData.exportData && currentProject && currentUser) {
      exportToExcel(exportData.exportData, resolvedFilename, exportData.name);
      toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
    } else {
      toast.error(TOAST_MESSAGES.EXPORT.MISSING_INFO);
    }
  };

  const handleExportDrawioPng = async () => {
    if (exportData.type === "drawio" && exportData.exportData && currentProject && currentUser) {
      try {
        if (exportData.exportData.drawioRef?.current) {
          try {
            if (typeof exportData.exportData.drawioRef.current.getPngData === "function") {
              await exportData.exportData.drawioRef.current.getPngData({
                filename: resolvedFilename || sanitizeFilename(exportData.name),
              });
            } else {
              exportData.exportData.drawioRef.current.exportDiagram({
                format: "png",
                filename: resolvedFilename || sanitizeFilename(exportData.name),
              });
            }
          } catch (exportError) {
            console.error("Export error:", exportError);
            toast.error(TOAST_MESSAGES.EXPORT.ERROR);
          }
          return;
        }

        const xmlData = exportData.exportData.xml;
        if (!xmlData) {
          toast.error(TOAST_MESSAGES.EXPORT.XML_NOT_FOUND);
          return;
        }

        if (xmlData.includes("base64")) {
          try {
            const base64Parts = xmlData.split(",");
            if (base64Parts.length < 2) {
              throw new Error("Invalid base64 format");
            }

            const base64Data = base64Parts[1];
            const dataType = base64Parts[0];

            if (dataType?.includes("svg")) {
              toast.error(
                "SVG形式のデータからPNGへの変換ができません。再度エクスポートしてください。",
              );
              return;
            }

            const byteCharacters = atob(base64Data!);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });
            const downloadFilename = `${resolvedFilename || sanitizeFilename(exportData.name)}.png`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = downloadFilename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
          } catch (error) {
            console.error("Base64処理エラー:", error);
            toast.error(TOAST_MESSAGES.EXPORT.PNG_BASE64_ERROR);
          }
        } else {
          toast.error(TOAST_MESSAGES.EXPORT.PNG_NO_DATA);
        }
      } catch (error) {
        console.error("PNGエクスポート処理エラー:", error);
        toast.error(TOAST_MESSAGES.EXPORT.PNG_FAILED);
      }
    } else {
      toast.error(TOAST_MESSAGES.EXPORT.MISSING_INFO);
    }
  };

  const handleExportPdf = async () => {
    if (
      (exportData.type === "reverse-engineering" || exportData.type === "document") &&
      currentProject &&
      currentUser
    ) {
      try {
        // 一時的にモーダルを閉じて、背景のコンテンツを表示
        onClose();

        // 少し待ってからPDF化（モーダルが完全に閉じるのを待つ）
        setTimeout(async () => {
          try {
            let targetElement: Element | null = null;

            if (exportData.type === "reverse-engineering") {
              // リバースエンジニアリングの場合: タブコンテンツを取得
              const activePanel = document.querySelector('[role="tabpanel"]:not([hidden])');
              if (activePanel) {
                targetElement =
                  activePanel.querySelector('[data-export-root="reverse-engineering"]') ||
                  activePanel;
              }
            } else if (exportData.type === "document") {
              // システム概要書の場合: より具体的にコンテンツ要素を取得
              targetElement = document.querySelector('[class*="innerDiv"]');
              if (!targetElement) {
                // フォールバック1: markdown-content や content-container
                targetElement = document.querySelector(
                  '[class*="markdown-content"], [class*="content-container"], [class*="content"]',
                );
              }
              if (!targetElement) {
                // フォールバック2: wrapper系のクラスを探す
                targetElement = document.querySelector('[class*="wrapper"]');
              }
              if (!targetElement) {
                // フォールバック3: メインコンテナを取得
                targetElement = document.querySelector('main, .main, [role="main"]');
              }
            }

            if (!targetElement) {
              toast.error(TOAST_MESSAGES.EXPORT.TARGET_NOT_FOUND);
              return;
            }

            // クローンを作成してスタイルを調整
            const clone = targetElement.cloneNode(true) as HTMLElement;
            clone.style.background = "white";
            clone.style.padding = "15px";
            clone.style.width = "100%";
            clone.style.overflow = "visible";
            clone.style.boxSizing = "border-box";
            clone.style.fontSize = "11px";
            clone.style.lineHeight = "1.4";

            // アクションボタン（編集・エクスポートボタン）を削除
            const actionButtons = clone.querySelector(
              '[class*="actionButtons"], [class*="buttons"]',
            );
            if (actionButtons) {
              actionButtons.remove();
            }

            // ブレッドクラムやナビゲーション要素を削除
            const breadcrumbs = clone.querySelectorAll('[class*="breadcrumb"]');
            breadcrumbs.forEach((breadcrumb) => breadcrumb.remove());

            // その他の不要なUI要素を削除
            const unnecessaryElements = clone.querySelectorAll(
              'button, [class*="chat"], [class*="modal"]',
            );
            unnecessaryElements.forEach((element) => element.remove());

            // すべての子要素のフォントサイズを調整
            const allChildren = clone.querySelectorAll("*");
            allChildren.forEach((el) => {
              const element = el as HTMLElement;
              const currentFontSize = window.getComputedStyle(element).fontSize;
              const sizeInPx = parseFloat(currentFontSize);
              if (sizeInPx > 12) {
                element.style.fontSize = `${Math.max(10, sizeInPx * 0.75)}px`;
              }
            });

            // ヘッダーを追加
            const header = document.createElement("div");
            header.style.marginBottom = "15px";
            header.style.borderBottom = "1px solid #333";
            header.style.paddingBottom = "8px";
            header.style.pageBreakAfter = "avoid";

            // タイトル
            const title = document.createElement("h1");
            if (exportData.type === "reverse-engineering") {
              const reData = exportData.exportData as ReverseEngineeringExportData;
              title.textContent =
                reData.type === "dependency-analysis" ? "依存関係分析レポート" : "要件分析レポート";
            } else if (exportData.type === "document") {
              title.textContent = getDocumentTypeJapaneseName(exportData.name);
            }
            title.style.fontSize = "18px";
            title.style.fontWeight = "bold";
            title.style.marginBottom = "6px";
            title.style.color = "#333";

            // 日付とプロジェクト情報
            const info = document.createElement("div");
            info.style.fontSize = "10px";
            info.style.color = "#666";
            info.style.display = "flex";
            info.style.justifyContent = "space-between";
            info.style.alignItems = "center";

            const dateInfo = document.createElement("span");
            const now = new Date();
            const year = now.getFullYear();
            const month = (now.getMonth() + 1).toString().padStart(2, "0");
            const day = now.getDate().toString().padStart(2, "0");
            const hours = now.getHours().toString().padStart(2, "0");
            const minutes = now.getMinutes().toString().padStart(2, "0");
            dateInfo.textContent = `出力日時: ${year}年${month}月${day}日 ${hours}:${minutes}`;

            const projectInfo = document.createElement("span");
            projectInfo.textContent = `プロジェクト: ${currentProject?.name || ""}`;

            info.appendChild(dateInfo);
            info.appendChild(projectInfo);

            header.appendChild(title);
            header.appendChild(info);

            // フッターを追加
            const footer = document.createElement("div");
            footer.style.marginTop = "15px";
            footer.style.paddingTop = "8px";
            footer.style.borderTop = "1px solid #ccc";
            footer.style.fontSize = "9px";
            footer.style.color = "#999";
            footer.style.textAlign = "center";
            footer.style.pageBreakBefore = "avoid";
            if (exportData.type === "reverse-engineering") {
              footer.textContent = `${currentProject?.name || "REQXIGN"} - リバースエンジニアリングレポート`;
            } else if (exportData.type === "document") {
              footer.textContent = `${currentProject?.name || "REQXIGN"} - ${getDocumentTypeJapaneseName(exportData.name)}`;
            }

            // ラッパーを作成してヘッダーとコンテンツを含める
            const wrapper = document.createElement("div");
            wrapper.style.background = "white";
            wrapper.style.padding = "10px";
            wrapper.style.width = "100%";
            wrapper.style.boxSizing = "border-box";
            wrapper.appendChild(header);
            wrapper.appendChild(clone);
            wrapper.appendChild(footer);

            // テーブルの調整 - より細かく制御
            const tables = clone.querySelectorAll("table");
            tables.forEach((table) => {
              const htmlTable = table as HTMLElement;
              htmlTable.style.width = "100%";
              htmlTable.style.tableLayout = "auto";
              htmlTable.style.fontSize = "9px";
              htmlTable.style.borderCollapse = "collapse";
              htmlTable.style.pageBreakInside = "auto";

              // テーブルのセルも調整
              const cells = htmlTable.querySelectorAll("td, th");
              cells.forEach((cell) => {
                const htmlCell = cell as HTMLElement;
                htmlCell.style.padding = "3px 5px";
                htmlCell.style.fontSize = "9px";
                htmlCell.style.wordBreak = "break-word";
                htmlCell.style.overflowWrap = "break-word";
                htmlCell.style.verticalAlign = "top";
              });
            });

            // 画像の調整
            const images = clone.querySelectorAll("img");
            images.forEach((img) => {
              const htmlImg = img as HTMLElement;
              htmlImg.style.maxWidth = "100%";
              htmlImg.style.height = "auto";
              htmlImg.style.display = "block";
              htmlImg.style.margin = "5px auto";
            });

            // カードやボックス要素のパディングとマージンを調整
            const cards = clone.querySelectorAll(
              '[class*="card"], [class*="Card"], [class*="box"], [class*="Box"]',
            );
            cards.forEach((card) => {
              const htmlCard = card as HTMLElement;
              htmlCard.style.padding = "6px";
              htmlCard.style.marginBottom = "6px";
              htmlCard.style.pageBreakInside = "avoid";
            });

            // リスト要素のマージンを調整
            const lists = clone.querySelectorAll("ul, ol");
            lists.forEach((list) => {
              const htmlList = list as HTMLElement;
              htmlList.style.paddingLeft = "15px";
              htmlList.style.marginTop = "3px";
              htmlList.style.marginBottom = "3px";
              htmlList.style.fontSize = "10px";
            });

            const listItems = clone.querySelectorAll("li");
            listItems.forEach((item) => {
              const htmlItem = item as HTMLElement;
              htmlItem.style.marginBottom = "2px";
              htmlItem.style.pageBreakInside = "avoid";
              htmlItem.style.fontSize = "10px";
            });

            // 見出しの調整
            const headings = clone.querySelectorAll("h1, h2, h3, h4, h5, h6");
            headings.forEach((heading) => {
              const htmlHeading = heading as HTMLElement;
              const tagName = heading.tagName.toLowerCase();
              if (tagName === "h1") htmlHeading.style.fontSize = "14px";
              else if (tagName === "h2") htmlHeading.style.fontSize = "13px";
              else if (tagName === "h3") htmlHeading.style.fontSize = "12px";
              else htmlHeading.style.fontSize = "11px";
              htmlHeading.style.marginTop = "8px";
              htmlHeading.style.marginBottom = "4px";
              htmlHeading.style.pageBreakAfter = "avoid";
            });

            // パラグラフの調整
            const paragraphs = clone.querySelectorAll("p");
            paragraphs.forEach((p) => {
              const htmlP = p as HTMLElement;
              htmlP.style.marginTop = "3px";
              htmlP.style.marginBottom = "3px";
              htmlP.style.fontSize = "10px";
            });

            // 一時的にドキュメントに追加（A4サイズ）
            const tempContainer = document.createElement("div");
            tempContainer.style.position = "absolute";
            tempContainer.style.left = "-9999px";
            tempContainer.style.top = "0";
            tempContainer.style.width = "210mm"; // A4の幅
            tempContainer.appendChild(wrapper);
            document.body.appendChild(tempContainer);

            // コンテンツの幅をチェック
            const contentWidth = wrapper.scrollWidth;
            const hasWideTable =
              tables.length > 0 &&
              Array.from(tables).some((table) => (table as HTMLElement).scrollWidth > 600);
            const isWide = hasWideTable || contentWidth > 800;

            // PDF生成設定を最適化
            await exportElementToPdf(wrapper, {
              filename: `${resolvedFilename}.pdf`,
              orientation: isWide ? "landscape" : "portrait",
              format: "a4",
              quality: 0.98,
              scale: 2.5, // 高解像度でキャプチャしつつ、コンテンツを適切にスケール
              margin: isWide ? [10, 10, 10, 10] : [15, 12, 15, 12], // 向きに応じて余白調整
            });

            // クリーンアップ
            document.body.removeChild(tempContainer);

            toast.success(TOAST_MESSAGES.EXPORT.PDF_SUCCESS);
          } catch (error) {
            console.error("PDFエクスポートエラー:", error);
            toast.error(TOAST_MESSAGES.EXPORT.PDF_FAILED);
          }
        }, 500);
      } catch (error) {
        console.error("PDFエクスポートエラー:", error);
        toast.error(TOAST_MESSAGES.EXPORT.PDF_FAILED);
      }
    } else {
      toast.error(TOAST_MESSAGES.EXPORT.MISSING_INFO);
    }
  };

  const handleExportDrawioSvg = async () => {
    if (exportData.type === "drawio" && exportData.exportData && currentProject && currentUser) {
      try {
        if (exportData.exportData.drawioRef?.current) {
          exportData.exportData.drawioRef.current.exportDiagram({
            format: "svg",
            filename: resolvedFilename || sanitizeFilename(exportData.name),
          });
          return;
        }

        const xmlData = exportData.exportData.xml;
        if (!xmlData) {
          toast.error(TOAST_MESSAGES.EXPORT.XML_NOT_FOUND);
          return;
        }

        if (xmlData.includes("base64")) {
          try {
            const base64Parts = xmlData.split(",");
            if (base64Parts.length < 2) {
              throw new Error("Invalid base64 format");
            }

            const base64Data = base64Parts[1];
            const byteCharacters = atob(base64Data!);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/svg+xml" });
            const downloadFilename = `${resolvedFilename || sanitizeFilename(exportData.name)}.svg`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = downloadFilename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
          } catch (error) {
            console.error("Base64処理エラー:", error);
            toast.error(TOAST_MESSAGES.EXPORT.SVG_BASE64_ERROR);
          }
        } else {
          try {
            const blob = new Blob([xmlData], { type: "image/svg+xml" });
            const downloadFilename = `${resolvedFilename || sanitizeFilename(exportData.name)}.svg`;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = downloadFilename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            toast.success(TOAST_MESSAGES.EXPORT.SUCCESS);
          } catch (error) {
            console.error("XML処理エラー:", error);
            toast.error(TOAST_MESSAGES.EXPORT.SVG_XML_ERROR);
          }
        }
      } catch (error) {
        console.error("SVGエクスポート処理エラー:", error);
        toast.error(TOAST_MESSAGES.EXPORT.SVG_FAILED);
      }
    } else {
      toast.error(TOAST_MESSAGES.EXPORT.MISSING_INFO);
    }
  };

  return (
    <div className={styles["modalOverlay"]}>
      <div className={styles["modalContent"]}>
        <div className={styles["modalHeader"]}>
          <h2>エクスポートタイプを選択</h2>
          <label className={styles["label"]}>ファイル名：</label>
          <input
            type="text"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className={styles["filenameInput"]}
            placeholder="ファイル名を入力"
          />
          <div className={styles["importActions"]} data-testid="export-modal-actions">
            {exportData.type === "document" ? (
              <>
                {/* システム概要書など特定のドキュメントのみPDF出力を許可 */}
                {exportData.name === "system-proposal" && (
                  <button
                    className={styles["selectButton"]}
                    onClick={handleExportPdf}
                    data-testid="export-pdf"
                  >
                    PDFファイル（.pdf）
                  </button>
                )}
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportMarkdown}
                  data-testid="export-markdown"
                >
                  マークダウンファイル（.md）
                </button>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportDocx}
                  data-testid="export-docx"
                >
                  Wordファイル（.docx）
                </button>
              </>
            ) : exportData.type === "table" ? (
              <>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportMarkdown}
                  data-testid="export-markdown"
                >
                  マークダウンファイル（.md）
                </button>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportXlsx}
                  data-testid="export-xlsx"
                >
                  エクセルファイル（.xlsx）
                </button>
              </>
            ) : exportData.type === "drawio" ? (
              <>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportDrawioSvg}
                  data-testid="export-svg"
                >
                  SVGファイル（.svg）
                </button>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportDrawioPng}
                  data-testid="export-png"
                >
                  PNGファイル（.png）
                </button>
              </>
            ) : exportData.type === "reverse-engineering" ? (
              <>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportPdf}
                  data-testid="export-pdf"
                >
                  PDFファイル（.pdf）
                </button>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportMarkdown}
                  data-testid="export-markdown"
                >
                  マークダウンファイル（.md）
                </button>
                <button
                  className={styles["selectButton"]}
                  onClick={handleExportDocx}
                  data-testid="export-docx"
                >
                  Wordファイル（.docx）
                </button>
              </>
            ) : null}
          </div>
          <div className={styles["actions"]}>
            <button className={styles["cancelButton"]} onClick={onClose}>
              キャンセル
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
